import { Pair, Polygon } from 'polygon-clipping';

import { ImageMask } from 'editor/src/store/design/types';

import { ObjectRect } from '../types';

// enough segments for the polygonal approximation to be indistinguishable from a curve at any editor zoom
export const ELLIPSE_SEGMENTS = 128;
export const CORNER_SEGMENTS = 24;
// circle, heart and arch are sampled the same way preflight samples them (src/helpers/image_mask.py), keep in sync
export const OUTLINE_SEGMENTS = 256;

// the classic parametric heart, scaled to fill a 1 x 1 box, y down
const HEART_UNIT_POINTS: Pair[] = (() => {
  const raw: Pair[] = [];
  for (let i = 0; i < OUTLINE_SEGMENTS; i += 1) {
    const t = (2 * Math.PI * i) / OUTLINE_SEGMENTS;
    const x = 16 * Math.sin(t) ** 3;
    const y = 13 * Math.cos(t) - 5 * Math.cos(2 * t) - 2 * Math.cos(3 * t) - Math.cos(4 * t);
    raw.push([x, -y]);
  }
  const xs = raw.map(([x]) => x);
  const ys = raw.map(([, y]) => y);
  const [minX, maxX, minY, maxY] = [Math.min(...xs), Math.max(...xs), Math.min(...ys), Math.max(...ys)];
  return raw.map(([x, y]) => [(x - minX) / (maxX - minX), (y - minY) / (maxY - minY)]);
})();

function getOutlinePoints(shape: 'circle' | 'heart' | 'arch', width: number, height: number): Pair[] {
  const n = OUTLINE_SEGMENTS;
  if (shape === 'circle') {
    // the largest circle centred in the frame
    const r = Math.min(width, height) / 2;
    return Array.from({ length: n }, (_, i): Pair => {
      const t = (2 * Math.PI * i) / n;
      return [width / 2 + r * Math.cos(t), height / 2 + r * Math.sin(t)];
    });
  }
  if (shape === 'arch') {
    // full-width semi-ellipse on a rectangle; the rise is half the width, less in a frame shorter than that
    const rise = Math.min(width / 2, height);
    const top = Array.from({ length: n + 1 }, (_, i): Pair => {
      const t = Math.PI + (Math.PI * i) / n;
      return [width / 2 + (width / 2) * Math.cos(t), rise + rise * Math.sin(t)];
    });
    return [...top, [width, height], [0, height]];
  }
  return HEART_UNIT_POINTS.map(([x, y]) => [x * width, y * height]);
}

export function getMaskCornerRadius(mask: ImageMask, width: number, height: number, radiusPx: number) {
  if (mask.shape !== 'rounded-rect') {
    return 0;
  }
  return Math.max(0, Math.min(radiusPx, width / 2, height / 2));
}

/**
 * Points of the mask shape inscribed in a width x height box, in box-local coordinates (origin = top left).
 */
export function getLocalMaskPoints(mask: ImageMask, width: number, height: number, radiusPx: number): Pair[] {
  const points: Pair[] = [];
  if (mask.shape === 'ellipse') {
    const rx = width / 2;
    const ry = height / 2;
    for (let i = 0; i < ELLIPSE_SEGMENTS; i += 1) {
      const t = (i / ELLIPSE_SEGMENTS) * 2 * Math.PI;
      points.push([rx + rx * Math.cos(t), ry + ry * Math.sin(t)]);
    }
    return points;
  }
  if (mask.shape === 'circle' || mask.shape === 'heart' || mask.shape === 'arch') {
    return getOutlinePoints(mask.shape, width, height);
  }

  const r = getMaskCornerRadius(mask, width, height, radiusPx);
  if (r === 0) {
    return [
      [0, 0],
      [width, 0],
      [width, height],
      [0, height],
    ];
  }

  // corner centers, clockwise starting top-right, each with the start angle of its quarter arc
  const corners: [number, number, number][] = [
    [width - r, r, -Math.PI / 2],
    [width - r, height - r, 0],
    [r, height - r, Math.PI / 2],
    [r, r, Math.PI],
  ];
  corners.forEach(([cx, cy, start]) => {
    for (let i = 0; i <= CORNER_SEGMENTS; i += 1) {
      const t = start + (i / CORNER_SEGMENTS) * (Math.PI / 2);
      points.push([cx + r * Math.cos(t), cy + r * Math.sin(t)]);
    }
  });
  return points;
}

/**
 * Mask outline as a closed polygon in canvas coordinates. The rect is rotated around its top-left corner,
 * same convention as rotatedRectToPolygon, so it can be intersected with the content clip polygons.
 */
function getMaskPolygon(rect: ObjectRect, mask: ImageMask, radiusPx: number): Polygon {
  const angle = (rect.angle * Math.PI) / 180;
  const cos = Math.cos(angle);
  const sin = Math.sin(angle);
  const ring: Pair[] = getLocalMaskPoints(mask, rect.width, rect.height, radiusPx).map(([x, y]) => [
    rect.left + x * cos - y * sin,
    rect.top + x * sin + y * cos,
  ]);
  ring.push([ring[0][0], ring[0][1]]);
  return [ring];
}

export default getMaskPolygon;

/**
 * Mask polygon of an image element for the given rect, or undefined when the element has no mask.
 * `scale` lets callers derive the mask of a scaled copy of the frame (e.g. the drop shadow).
 */
export function getElementMaskPolygon(
  element: { type: string; mask?: ImageMask },
  rect: ObjectRect,
  mm2px: (size: number) => number,
  scale = 1,
): Polygon | undefined {
  if (element.type !== 'image' || !element.mask) {
    return undefined;
  }
  return getMaskPolygon(rect, element.mask, mm2px(element.mask.radius ?? 0) * scale);
}
